/**
 * App-wide export toast.
 *
 * Manuscript, map, and chronicle exports report here. `ExportToast.vue`
 * renders the model. Reveal and open stay Cmds so the pure update never
 * touches the opener plugin.
 */
import { computed, shallowRef, type ComputedRef } from "vue";
import { openPath, revealItemInDir } from "@tauri-apps/plugin-opener";
import { assertNever } from "../lib/assertNever";
import { isNothing, type Maybe } from "../lib/maybe";
import { isTauri } from "../lib/runtime";
import { isMobileShell } from "../lib/tauri/core";
import { dispatchInto } from "../lib/tea";
import {
  initialExportToastModel,
  updateExportToast,
  type ExportNotice,
  type ExportToastCmd,
  type ExportToastModel,
  type ExportToastMsg,
} from "./useExportToast.update";

const holder = shallowRef<ExportToastModel>(initialExportToastModel);

const SHOW_FAILED = "Couldn't show that file in its folder.";
const OPEN_FAILED = "Couldn't open that file.";

function dispatchToast(msg: ExportToastMsg): void {
  void dispatchInto(holder, msg, updateExportToast, runExportToastCmd);
}

async function runOpener(
  path: string,
  open: (path: string) => Promise<void>,
  failure: string,
): Promise<void> {
  if (isTauri() === false) {
    dispatchToast({ type: "ActionFailed", message: failure });
    return;
  }
  try {
    await open(path);
  } catch (error) {
    console.error(failure, error);
    dispatchToast({ type: "ActionFailed", message: failure });
  }
}

async function runExportToastCmd(cmd: ExportToastCmd): Promise<void> {
  switch (cmd.kind) {
    case "revealInFolder":
      await runOpener(cmd.path, revealItemInDir, SHOW_FAILED);
      return;
    case "openPath":
      await runOpener(cmd.path, openPath, OPEN_FAILED);
      return;
    default:
      assertNever(cmd);
  }
}

export function useExportToast(): {
  notice: ComputedRef<ExportNotice>;
  dismiss: () => void;
  showInFolder: () => void;
  openFile: () => void;
} {
  const notice = computed(() => holder.value.notice);

  function dismiss(): void {
    dispatchToast({ type: "Dismiss" });
  }

  function showInFolder(): void {
    dispatchToast({ type: "ShowInFolder" });
  }

  function openFile(): void {
    dispatchToast({ type: "Open" });
  }

  return { notice, dismiss, showInFolder, openFile };
}

export function dismissExportToast(): void {
  dispatchToast({ type: "Dismiss" });
}

export function reportExportInfo(message: string): void {
  dispatchToast({ type: "Info", message });
}

export function reportExportFailed(message: string): void {
  dispatchToast({ type: "Failed", message });
}

/**
 * A file written through the native save dialog. No toast when the dialog
 * was cancelled (`nothing`). The Android/iOS shell gets a confirmation
 * without Show in folder or Open.
 */
export function reportSavedExport(format: string, saved: Maybe<string>): void {
  if (isNothing(saved)) return;
  const path = saved.value;
  void isMobileShell().then((mobile) => {
    dispatchToast({
      type: "Saved",
      format,
      target: mobile ? { kind: "device", path } : { kind: "desktop", path },
    });
  });
}

/** Browser download. The toast has no folder or default-app actions. */
export function reportDownloadExport(format: string): void {
  dispatchToast({
    type: "Saved",
    format,
    target: { kind: "download" },
  });
}
